import React, { useState, useEffect, useMemo } from 'react';
import { useNotification } from '../../../../context/NotificationContext';
import api from '../../../../api/api';
import styles from './TemplateManager.module.css';

/**
 * TemplateManager - Phase 3E
 * 
 * Template library and management with:
 * - Browse/search templates
 * - Filter by category, visibility, usage
 * - Template cards with metrics
 * - Create/Edit/Delete actions
 * - Permission checks
 */
const TemplateManager = ({ 
  clubId,
  onSelectTemplate,
  onCreateTemplate,
  onClose,
  selectedTemplateId = null
}) => {
  const { showSuccess, showError } = useNotification();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [visibilityFilter, setVisibilityFilter] = useState('all');
  const [usageFilter, setUsageFilter] = useState('all');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Category options
  const categories = [
    { value: 'all', label: 'All Categories', icon: '📋' },
    { value: 'youth', label: 'Youth Programs', icon: '👶' },
    { value: 'adult', label: 'Adult Programs', icon: '👨' },
    { value: 'competitive', label: 'Competitive', icon: '🏆' },
    { value: 'recreational', label: 'Recreational', icon: '⚽' },
    { value: 'camps', label: 'Camps', icon: '🏕️' },
    { value: 'clinics', label: 'Clinics', icon: '🎓' },
    { value: 'tournaments', label: 'Tournaments', icon: '🥇' }
  ];

  // Visibility options
  const visibilityOptions = [
    { value: 'all', label: 'All Templates', icon: '📄' },
    { value: 'private', label: 'My Templates', icon: '🔒' },
    { value: 'club', label: 'Club Templates', icon: '🏢' },
    { value: 'public', label: 'Public Templates', icon: '🌐' }
  ];

  // Usage options
  const usageOptions = [
    { value: 'all', label: 'All', icon: '📊' },
    { value: 'used', label: 'In Use', icon: '✅' },
    { value: 'unused', label: 'Not Used', icon: '⭕' }
  ];

  // Fetch templates
  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const params = {
        clubId,
        limit: 100,
        includeUsage: true,
        includePublic: true
      };
      const data = await api.get('/api/registration/templates', { params });
      setTemplates(data.templates || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
      showError('Failed to load templates');
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, [clubId]);

  // Filter templates
  const filteredTemplates = useMemo(() => {
    return templates.filter(template => {
      // Category filter
      if (categoryFilter !== 'all' && template.category !== categoryFilter) return false;
      
      // Visibility filter
      if (visibilityFilter !== 'all' && template.visibility !== visibilityFilter) return false;
      
      // Usage filter
      if (usageFilter === 'used' && (template.usage?.timesUsed || 0) === 0) return false;
      if (usageFilter === 'unused' && (template.usage?.timesUsed || 0) > 0) return false;
      
      // Search filter
      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        const name = (template.name || '').toLowerCase();
        const desc = (template.description || '').toLowerCase();
        const creator = typeof template.createdBy === 'string' 
          ? template.createdBy.toLowerCase()
          : `${template.createdBy?.firstName || ''} ${template.createdBy?.lastName || ''}`.toLowerCase();
        
        if (!name.includes(search) && !desc.includes(search) && !creator.includes(search)) {
          return false;
        }
      }
      
      return true;
    });
  }, [templates, categoryFilter, visibilityFilter, usageFilter, searchTerm]);

  // Get creator name
  const getCreatorName = (template) => {
    if (typeof template.createdBy === 'string') return template.createdBy;
    if (template.createdBy?.firstName && template.createdBy?.lastName) {
      return `${template.createdBy.firstName} ${template.createdBy.lastName}`;
    }
    return template.createdBy?.email || 'Unknown';
  };

  // Get category icon
  const getCategoryIcon = (category) => {
    return categories.find(c => c.value === category)?.icon || '📋';
  };

  // Get visibility icon
  const getVisibilityIcon = (visibility) => {
    return visibilityOptions.find(v => v.value === visibility)?.icon || '📄';
  };

  // Check permissions (simplified - in real app would check actual user)
  const canEdit = (template) => {
    // For now, allow editing all templates
    return true;
  };

  const canDelete = (template) => {
    // Can't delete templates that are in use
    return (template.usage?.timesUsed || 0) === 0;
  };

  // Handle delete
  const handleDelete = async () => {
    if (!deleteConfirm) return;
    
    setDeleting(true);
    try {
      await api.del(`/api/registration/templates/${deleteConfirm._id}`);
      showSuccess('Template deleted');
      setTemplates(templates.filter(t => t._id !== deleteConfirm._id));
      setDeleteConfirm(null);
    } catch (error) {
      showError(error.message || 'Failed to delete template');
    } finally {
      setDeleting(false);
    }
  };

  // Clear filters
  const clearFilters = () => {
    setSearchTerm('');
    setCategoryFilter('all');
    setVisibilityFilter('all');
    setUsageFilter('all');
  };

  if (loading) {
    return (
      <div className={styles.templateManager}>
        <div className={styles.header}>
          <h2>📋 Template Library</h2>
          <button className={styles.closeBtn} onClick={onClose}>✕</button>
        </div>
        <div className={styles.loading}>
          <div className={styles.spinner}></div>
          <p>Loading templates...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.templateManager}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h2>📋 Template Library</h2>
          <p>Browse and manage registration templates</p>
        </div>
        <button className={styles.closeBtn} onClick={onClose}>✕</button>
      </div>

      {/* Controls */}
      <div className={styles.controls}>
        <div className={styles.filters}>
          <select 
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className={styles.filterSelect}
          >
            {categories.map(cat => (
              <option key={cat.value} value={cat.value}>
                {cat.icon} {cat.label}
              </option>
            ))}
          </select>
          
          <select 
            value={visibilityFilter}
            onChange={(e) => setVisibilityFilter(e.target.value)}
            className={styles.filterSelect}
          >
            {visibilityOptions.map(opt => (
              <option key={opt.value} value={opt.value}>
                {opt.icon} {opt.label}
              </option>
            ))}
          </select>
          
          <select 
            value={usageFilter}
            onChange={(e) => setUsageFilter(e.target.value)}
            className={styles.filterSelect}
          >
            {usageOptions.map(opt => (
              <option key={opt.value} value={opt.value}>
                {opt.icon} {opt.label}
              </option>
            ))}
          </select>
        </div>
        
        <div className={styles.searchRow}>
          <div className={styles.searchBox}>
            <span className={styles.searchIcon}>🔍</span>
            <input
              type="text"
              placeholder="Search templates..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={styles.searchInput}
            />
          </div>
          
          <div className={styles.actions}>
            <button className={styles.refreshBtn} onClick={fetchTemplates}>
              🔄
            </button>
            <button className={styles.createBtn} onClick={() => onCreateTemplate(null)}>
              ➕ New Template
            </button>
          </div>
        </div>
        
        <div className={styles.resultsInfo}>
          <span className={styles.count}>{filteredTemplates.length}</span> of {templates.length} templates
          {(categoryFilter !== 'all' || visibilityFilter !== 'all' || usageFilter !== 'all' || searchTerm) && (
            <button className={styles.clearBtn} onClick={clearFilters}>
              Clear filters
            </button>
          )}
        </div>
      </div>

      {/* Templates Grid */}
      <div className={styles.templatesContainer}>
        {filteredTemplates.length === 0 ? (
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon}>📋</div>
            <h3>No templates found</h3>
            <p>No templates match your current filters</p>
            <div className={styles.emptyActions}>
              <button className={styles.clearBtn} onClick={clearFilters}>
                Clear Filters
              </button>
              <button className={styles.createBtn} onClick={() => onCreateTemplate(null)}>
                Create Template
              </button>
            </div>
          </div>
        ) : (
          <div className={styles.templatesGrid}>
            {filteredTemplates.map(template => (
              <div 
                key={template._id} 
                className={`${styles.templateCard} ${selectedTemplateId === template._id ? styles.selected : ''}`}
              >
                <div className={styles.cardHeader}>
                  <div className={styles.titleRow}>
                    <h3>{template.name}</h3>
                    <span className={styles.visibilityBadge}>
                      {getVisibilityIcon(template.visibility)}
                    </span>
                  </div>
                  <span className={styles.categoryBadge}>
                    {getCategoryIcon(template.category)} {template.category}
                  </span>
                </div>
                
                <p className={styles.description}>
                  {template.description || 'No description provided'}
                </p>
                
                <div className={styles.metrics}>
                  <div className={styles.metric}>
                    <span className={styles.metricIcon}>📄</span>
                    <span className={styles.metricValue}>{template.sections?.length || 0}</span>
                    <span className={styles.metricLabel}>Sections</span>
                  </div>
                  <div className={styles.metric}>
                    <span className={styles.metricIcon}>📎</span>
                    <span className={styles.metricValue}>{template.documentRequirements?.length || 0}</span>
                    <span className={styles.metricLabel}>Docs</span>
                  </div>
                  <div className={styles.metric}>
                    <span className={styles.metricIcon}>👥</span>
                    <span className={styles.metricValue}>{template.usage?.timesUsed || 0}</span>
                    <span className={styles.metricLabel}>Uses</span>
                  </div>
                  <div className={styles.metric}>
                    <span className={styles.metricIcon}>⭐</span>
                    <span className={styles.metricValue}>{template.usage?.averageRating?.toFixed(1) || '—'}</span>
                    <span className={styles.metricLabel}>Rating</span>
                  </div>
                </div>
                
                <div className={styles.cardFooter}>
                  <span className={styles.creator}>
                    By: {getCreatorName(template)}
                  </span>
                  
                  <div className={styles.cardActions}>
                    <button 
                      className={styles.selectBtn}
                      onClick={() => onSelectTemplate(template)}
                    >
                      Use
                    </button>
                    {canEdit(template) && (
                      <button 
                        className={styles.editBtn}
                        onClick={() => onCreateTemplate(template)}
                        title="Edit"
                      >
                        ✏️
                      </button>
                    )}
                    {canDelete(template) && (
                      <button 
                        className={styles.deleteBtn}
                        onClick={() => setDeleteConfirm(template)}
                        title="Delete"
                      >
                        🗑️
                      </button>
                    )}
                  </div>
                </div>
                
                {(template.usage?.timesUsed || 0) > 0 && (
                  <div className={styles.activeIndicator}>
                    🟢 Active in {template.usage.timesUsed} program{template.usage.timesUsed !== 1 ? 's' : ''}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className={styles.modalOverlay} onClick={() => setDeleteConfirm(null)}>
          <div className={styles.modal} onClick={e => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>⚠️ Delete Template</h3>
            </div>
            <div className={styles.modalBody}>
              <p>Are you sure you want to delete:</p>
              <p className={styles.templateName}>"{deleteConfirm.name}"</p>
              <p className={styles.warning}>This action cannot be undone.</p>
              {(deleteConfirm.usage?.timesUsed || 0) > 0 && (
                <div className={styles.usageWarning}>
                  ⚠️ This template is used by {deleteConfirm.usage.timesUsed} program(s)
                </div>
              )}
            </div>
            <div className={styles.modalFooter}>
              <button 
                className={styles.cancelBtn}
                onClick={() => setDeleteConfirm(null)}
              >
                Cancel
              </button>
              <button 
                className={styles.confirmDeleteBtn}
                onClick={handleDelete}
                disabled={deleting}
              >
                {deleting ? 'Deleting...' : '🗑️ Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TemplateManager;
